import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../components/Loader";
import { canCreateEmailAccounts } from "../../utils/userAccess";
import { crmHeaders } from "../../utils/crmApi";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function guessMailHosts(email) {
    const domain = (email.split("@")[1] || "").toLowerCase();
    if (!domain) {
        return { smtp: "", imap: "", port: "587" };
    }
    if (domain === "gmail.com" || domain === "googlemail.com") {
        return { smtp: "smtp.gmail.com", imap: "imap.gmail.com", port: "587" };
    }
    if (domain === "outlook.com" || domain === "hotmail.com" || domain === "live.com") {
        return { smtp: "smtp.office365.com", imap: "outlook.office365.com", port: "587" };
    }
    if (domain === "yahoo.com") {
        return { smtp: "smtp.mail.yahoo.com", imap: "imap.mail.yahoo.com", port: "587" };
    }
    return { smtp: "", imap: "", port: "587" };
}

export default function AddEmail() {
    const API_URL = BASE_URL + "/addemail";
    const DETECT_URL = BASE_URL + "/detectmailsettings";
    const GET_COUNTRY_URL = BASE_URL + "/getcountries";
    const navigate = useNavigate();
    const detectTimer = useRef(null);

    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [hostName, setHostName] = useState("");
    const [imapHost, setImapHost] = useState("");
    const [port, setPort] = useState("587");
    const [emailValue, setEmailValue] = useState("");
    const [providerLabel, setProviderLabel] = useState("");
    const [hostTouched, setHostTouched] = useState(false);
    const [imapTouched, setImapTouched] = useState(false);

    useEffect(() => {
        axios
            .get(GET_COUNTRY_URL, { headers: crmHeaders() })
            .then((res) => {
                setCountries(res.data || []);
                setLoading(false);
            })
            .catch(() => {
                toast.error("Failed to load countries");
                setLoading(false);
            });
    }, []);

    const fetchDetectedSettings = (email) => {
        const trimmed = String(email || "").trim().toLowerCase();
        if (!trimmed.includes("@")) {
            setProviderLabel("");
            return;
        }

        setDetecting(true);
        axios
            .get(`${DETECT_URL}/${encodeURIComponent(trimmed)}`, { headers: crmHeaders(), timeout: 15000 })
            .then((res) => {
                const data = res.data || {};
                setProviderLabel(data.providerLabel || "");
                if (!hostTouched && data.smtpHost) setHostName(data.smtpHost);
                if (!imapTouched && data.imapHost) setImapHost(data.imapHost);
                if (data.port) setPort(String(data.port));
            })
            .catch(() => {
                const guessed = guessMailHosts(trimmed);
                if (!hostTouched && guessed.smtp) setHostName(guessed.smtp);
                if (!imapTouched && guessed.imap) setImapHost(guessed.imap);
                setPort(guessed.port);
            })
            .finally(() => setDetecting(false));
    };

    const onEmailChange = (value) => {
        setEmailValue(value);
        const guessed = guessMailHosts(value);
        if (!hostTouched) setHostName(guessed.smtp);
        if (!imapTouched) setImapHost(guessed.imap);
        setPort(guessed.port);

        if (detectTimer.current) clearTimeout(detectTimer.current);
        detectTimer.current = setTimeout(() => fetchDetectedSettings(value), 250);
    };

    const onSmtpHostChange = (value) => {
        setHostTouched(true);
        setHostName(value);
        const v = value.trim().toLowerCase();
        if (!imapTouched) {
            if (v.startsWith("smtp.")) {
                setImapHost(v.replace(/^smtp\./, "imap."));
            } else if (v.startsWith("mail.")) {
                setImapHost(v);
            }
        }
    };

    const isGmail = /@(gmail|googlemail)\.com$/i.test(emailValue);

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData(e.target);
        const host = String(formData.get("hostname") || "").trim();
        const imap = String(formData.get("imaphost") || "").trim();
        const email = formData.get("email");
        const password = formData.get("password");
        const portVal = formData.get("port");
        const country = formData.get("country");

        if (!email || !password || !portVal || !country) {
            toast.error("Email, password, port, and country are required!");
            return;
        }

        setSubmitting(true);
        axios
            .post(
                API_URL,
                {
                    Port: portVal,
                    HostName: host,
                    ImapHost: imap,
                    Email: String(email).trim().toLowerCase(),
                    Password: password,
                    Country: country,
                },
                { headers: crmHeaders() }
            )
            .then((res) => {
                toast.success(res.data.message || "Email saved");
                navigate("/emails/check-emails");
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Could not add email — check password");
            })
            .finally(() => setSubmitting(false));
    };

    if (!canCreateEmailAccounts()) {
        return (
            <div className="container p-4">
                <h3>Access Denied</h3>
                <p className="text-muted">You do not have permission to create email accounts.</p>
            </div>
        );
    }

    if (loading) return <Loader />;

    return (
        <div className="container">
            <ToastContainer />
            <div className="crm-page-header mb-4">
                <div>
                    <h1 className="crm-page-title">Add New Email</h1>
                    <p className="crm-page-subtitle">
                        Email likho — Hostinger, Namecheap, Gmail, Outlook auto-detect hoga. Sirf password
                        zaroori hai.
                    </p>
                </div>
            </div>

            <div className="crm-user-form-card">
                <div className="crm-user-form-card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Email address</label>
                                <input
                                    type="email"
                                    placeholder="name@crowntradesmark.com"
                                    className="form-control"
                                    name="email"
                                    value={emailValue}
                                    onChange={(e) => onEmailChange(e.target.value)}
                                    required
                                />
                                {detecting ? (
                                    <small className="text-muted">Detecting mail provider...</small>
                                ) : providerLabel ? (
                                    <small className="text-success d-block mt-1">
                                        Detected: <strong>{providerLabel}</strong>
                                        {hostName ? ` — SMTP ${hostName}, IMAP ${imapHost || hostName}` : ""}
                                    </small>
                                ) : (
                                    <small className="text-muted">
                                        Domain ke MX records se provider auto-detect hota hai
                                    </small>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Country (CRM inbox group)</label>
                                <select className="form-control" name="country" required defaultValue="">
                                    <option value="" disabled>
                                        Select country
                                    </option>
                                    {countries.map((c) => (
                                        <option key={c.Id} value={c.Country}>
                                            {c.Country}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">SMTP Host (optional — auto-detect)</label>
                                <input
                                    type="text"
                                    placeholder="Auto — e.g. smtp.hostinger.com"
                                    className="form-control"
                                    name="hostname"
                                    value={hostName}
                                    onChange={(e) => onSmtpHostChange(e.target.value)}
                                />
                                <small className="text-muted d-block mt-1">
                                    Khali chhoro for auto-detect. Namecheap: cPanel Outgoing Server (premiumXXX.web-hosting.com)
                                </small>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">IMAP Host (optional — auto-detect)</label>
                                <input
                                    type="text"
                                    placeholder="Auto — e.g. imap.hostinger.com"
                                    className="form-control"
                                    name="imaphost"
                                    value={imapHost}
                                    onChange={(e) => {
                                        setImapTouched(true);
                                        setImapHost(e.target.value);
                                    }}
                                />
                                <small className="text-muted">
                                    mail.yourdomain.com tabhi jab DNS mein exist kare — warna provider IMAP use karein
                                </small>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">SMTP Port</label>
                                <select
                                    className="form-control"
                                    name="port"
                                    value={port}
                                    onChange={(e) => setPort(e.target.value)}
                                    required
                                >
                                    <option value="587">587 — TLS (recommended)</option>
                                    <option value="465">465 — SSL</option>
                                    <option value="25">25</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Email password</label>
                                <input
                                    type="password"
                                    placeholder={isGmail ? "16-char App Password" : "Mailbox password"}
                                    className="form-control"
                                    name="password"
                                    required
                                />
                                {isGmail ? (
                                    <small className="text-warning">
                                        Gmail: Google App Password chahiye (normal login password nahi)
                                    </small>
                                ) : (
                                    <small className="text-muted">
                                        Hosting mailbox password — cPanel / Hostinger email password
                                    </small>
                                )}
                            </div>
                        </div>
                        <div className="mt-4">
                            <button className="btn btn-primary" disabled={submitting || detecting}>
                                {submitting ? "Verifying & saving..." : "Add Email"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
